import { Control, Controller } from "react-hook-form";
import { DynamicFee, FeeCountUnit, SalaryDeductionType } from "../../../__generated__/graphql";
import { useI18n } from "../../../hooks/i18n-hooks";
import Gap from "../../../ui_atoms/Gap";
import { BodyMediumText, BodySmallText } from "../../FormattedText";
import { FormData } from "./PayrollBreakdownEditor";
import { useTheme } from "../../../hooks/use-theme";
import { formatCurrency, getAmountWithClientDeductions } from "../../../utils";
import VStack from "../../../ui_atoms/VStack";
import { ChainTextInputProps } from "../../../hooks/use-chain-input-focus";
import { View } from "react-native";
import QuantityField from "../../QuantityField";
import { useValidateFieldMinMax } from "../../../hooks/use-validate-field-min-max";

type DynamicFeesProps = {
    clientDeductions: SalaryDeductionType[];
    dynamicFees: DynamicFee[];
    control: Control<FormData>;
    chainInputFocus?: (fieldName: string) => ChainTextInputProps;
};

export const DynamicFees = ({
    dynamicFees,
    clientDeductions,
    control,
    chainInputFocus = () => ({}),
}: DynamicFeesProps) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const validateFieldMinMax = useValidateFieldMinMax();

    return (
        dynamicFees.length > 0 && (
            <>
                <Gap gap="1/3" />
                <VStack gap={1}>
                    {dynamicFees?.map((fee, index) => {
                        const feeName =
                            fee.fee.otherTitle || i18n.t(`fees.type.${fee.fee.type}.title`);
                        const hasUnit =
                            !!fee.fee.countUnit && fee.fee.countUnit !== FeeCountUnit.Default;

                        return (
                            <View key={`${fee.fee.id}`}>
                                <BodyMediumText>
                                    {hasUnit
                                        ? i18n.t(
                                              "payroll_breakdown.field_variable_fee_count_with_unit",
                                              {
                                                  feeName,
                                                  perUnit: i18n.t(
                                                      `fees.count_unit.${fee.fee.countUnit}`,
                                                  ),
                                              },
                                          )
                                        : i18n.t("payroll_breakdown.field_variable_fee_count", {
                                              feeName,
                                          })}
                                </BodyMediumText>
                                <Gap gap="1/3" />
                                <Controller
                                    name={`dynamicFees.${index}`}
                                    control={control}
                                    rules={{
                                        validate: {
                                            exists: (value) => {
                                                if (
                                                    value.count === undefined ||
                                                    value.count === null
                                                ) {
                                                    return i18n.t(
                                                        "payroll_breakdown.error_not_set",
                                                    );
                                                }
                                                return true;
                                            },
                                            minMax: (value) =>
                                                validateFieldMinMax({
                                                    value: value.count,
                                                    max: 100_000,
                                                }),
                                        },
                                    }}
                                    render={({
                                        field: { value, onChange, name },
                                        fieldState: { error },
                                    }) => {
                                        return (
                                            <QuantityField
                                                {...chainInputFocus(name)}
                                                onChange={(v) => {
                                                    onChange({
                                                        ...value,
                                                        count:
                                                            typeof v === "number" && !isNaN(v)
                                                                ? v
                                                                : undefined,
                                                    });
                                                }}
                                                error={error?.message}
                                                value={
                                                    value && value.count !== null
                                                        ? value.count
                                                        : undefined
                                                }
                                            />
                                        );
                                    }}
                                />
                                <Gap gap={1 / 3} />
                                <BodySmallText color={colors.greyAccessible}>
                                    {i18n.t("payroll_breakdown.field_variable_fee_count_info", {
                                        feeCount: formatCurrency({
                                            amountInCents: fee.fee.amount,
                                            displayDigitsWhenNoCents: false,
                                        }),
                                        withTaxes: formatCurrency({
                                            amountInCents: getAmountWithClientDeductions(
                                                fee.fee.amount,
                                                clientDeductions,
                                            ),
                                            displayDigitsWhenNoCents: false,
                                        }),
                                        perUnit:
                                            fee.fee.countUnit &&
                                            fee.fee.countUnit !== FeeCountUnit.Default
                                                ? i18n.t(
                                                      `fees.repetition.count_${fee.fee.countUnit}`,
                                                  )
                                                : i18n.t("fees.repetition.count"),
                                    })}
                                </BodySmallText>
                            </View>
                        );
                    })}
                </VStack>
            </>
        )
    );
};
